import React, { useState, useEffect, useContext } from 'react';
import { getOccupants, createOccupant, updateOccupant, deleteOccupant } from '../services/occupantService';
import { formatDate } from '../utils/dateUtils';
import { AuthContext } from '../contexts/AuthContext';
import PageWrapper from '../components/PageWrapper';
import OccupantModal from '../components/OccupantModal';

const OccupantList = () => {
  const { user, agencyId, agencyCountry } = useContext(AuthContext);
  const [occupants, setOccupants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOccupant, setSelectedOccupant] = useState(null);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  useEffect(() => {
    loadOccupants();
  }, []);

  const loadOccupants = async () => {
    try {
      setLoading(true);
      const data = await getOccupants(agencyId);
      setOccupants(data);
      setError(null);
    } catch (err) {
      setError('Failed to load occupants');
      console.error('Error loading occupants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOccupant = async (occupantData) => {
    try {
      const newOccupant = await createOccupant(agencyId, occupantData);
      setOccupants(prev => [...prev, newOccupant]);
    } catch (err) {
      setError('Failed to create occupant');
      console.error('Error creating occupant:', err);
    }
  };

  const handleUpdateOccupant = async (occupantData) => {
    try {
      const updatedOccupant = await updateOccupant(agencyId, selectedOccupant.id, occupantData);
      setOccupants(prev => prev.map(occ =>
        occ.id === selectedOccupant.id 
          ? updatedOccupant || { ...occ, ...occupantData } // Use merged data if server returns null
          : occ
      ));
    } catch (err) {
      setError('Failed to update occupant');
      console.error('Error updating occupant:', err);
    }
  };

  const handleDeleteOccupant = async (occupantId) => {
    if (window.confirm('Are you sure you want to delete this occupant?')) {
      try {
        await deleteOccupant(agencyId, occupantId);
        setOccupants(prev => prev.filter(occ => occ.id !== occupantId));
      } catch (err) {
        setError('Failed to delete occupant');
        console.error('Error deleting occupant:', err);
      }
    }
  };

  const handleSaveOccupant = (occupantData) => {
    if (selectedOccupant) {
      handleUpdateOccupant(occupantData);
    } else {
      handleCreateOccupant(occupantData);
    }
  };

  const handleEditOccupant = (occupant) => {
    setSelectedOccupant(occupant);
    setShowModal(true);
  };

  const handleAddOccupant = () => {
    setSelectedOccupant(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOccupant(null);
  };

  // Filter occupants based on search term
  const filteredOccupants = occupants.filter(occupant => {
    const given = occupant.person?.given || occupant.given || occupant.first_name || '';
    const surname = occupant.person?.surname || occupant.surname || occupant.last_name || '';
    const fullName = `${given} ${surname}`.trim().toLowerCase();
    const email = (occupant.person?.email || occupant.email || '').toLowerCase();
    const phoneVal = (occupant.person?.mobile || occupant.mobile || occupant.phone || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return fullName.includes(term) || email.includes(term) || phoneVal.includes(term);
  });

  if (loading) {
    return (
      <PageWrapper>
        <div className="container-fluid py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="mt-3 text-muted">Loading occupants...</h5>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h3 mb-1">
                  <i className="bi bi-people me-2"></i>
                  Occupants
                </h1>
                <p className="text-muted mb-0">Manage tenant and occupant information</p>
              </div>
              <button className="btn btn-primary" onClick={handleAddOccupant}>
                <i className="bi bi-person-plus me-2"></i>
                Add Occupant
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="row g-3 align-items-center">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-search me-2"></i>
                      Search Occupants
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Search by name, email, or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted">
                      <small>
                        Showing {filteredOccupants.length} of {occupants.length} occupants
                      </small>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Occupants List */}
        {filteredOccupants.length > 0 ? (
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Date of Birth</th>
                          <th>Emergency Contact</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOccupants.map((occupant) => (
                          <tr key={occupant.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-3">
                                  <i className="bi bi-person text-white"></i>
                                </div>
                                <div>
                                  <div className="fw-semibold">
                                    {occupant.person?.given || occupant.given || occupant.first_name || ''}{' '}
                                    {occupant.person?.surname || occupant.surname || occupant.last_name || ''}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div>{occupant.person?.email || occupant.email || '-'}</div>
                              </div>
                            </td>
                            <td>
                              <div>{occupant.person?.mobile || occupant.mobile || occupant.phone || '-'}</div>
                            </td>
                            <td>
                              <div>
                                {occupant.dateOfBirth
                                  ? formatDateWithContext(occupant.dateOfBirth)
                                  : '-'
                                }
                              </div>
                            </td>
                            <td>
                              <div>
                                {occupant.emergencyContactName ? (
                                  <>
                                    <div className="fw-medium">{occupant.emergencyContactName}</div>
                                    <small className="text-muted">{occupant.emergencyContactPhone}</small>
                                  </>
                                ) : (
                                  '-'
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditOccupant(occupant)}
                                  title="Edit Occupant"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteOccupant(occupant.id)}
                                  title="Delete Occupant"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <i className="bi bi-people display-1 text-muted mb-3"></i>
                <h4 className="text-muted mb-3">
                  {searchTerm ? 'No occupants found' : 'No occupants yet'}
                </h4>
                <p className="text-muted mb-4">
                  {searchTerm
                    ? "Try adjusting your search criteria."
                    : "Start by adding your first occupant to the system."
                  }
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  {searchTerm && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Clear Search
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={handleAddOccupant}
                  >
                    <i className="bi bi-person-plus me-2"></i>
                    Add Occupant
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Occupant Modal */}
        <OccupantModal
          show={showModal}
          onClose={handleCloseModal}
          onSave={handleSaveOccupant}
          occupant={selectedOccupant}
          agencyId={agencyId}
        />
      </div>
    </PageWrapper>
  );
};

export default OccupantList;
